// Procedural canvas textures (no image files, nothing fetched). Text is drawn with fillText only.
import { CanvasTexture, RepeatWrapping, SRGBColorSpace, ClampToEdgeWrapping, LinearMipmapLinearFilter } from 'three';

export const FONT_MONO = '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace';
export const FONT_SERIF = '"Instrument Serif", "Iowan Old Style", Georgia, serif';

function canvas(w, h = w) {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function tex(c, { srgb = false, repeat = false } = {}) {
  const t = new CanvasTexture(c);
  if (srgb) t.colorSpace = SRGBColorSpace;
  t.wrapS = t.wrapT = repeat ? RepeatWrapping : ClampToEdgeWrapping;
  t.anisotropy = 4;
  t.minFilter = LinearMipmapLinearFilter;
  return t;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Soft round blob (white, alpha falloff) for contact shadows. */
export function blobTexture() {
  const c = canvas(128);
  const g = c.getContext('2d');
  const grd = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.35, 'rgba(255,255,255,0.72)');
  grd.addColorStop(0.7, 'rgba(255,255,255,0.22)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, 128, 128);
  return tex(c);
}

/**
 * Rounded-rectangle ambient occlusion for nine-slice use: the rectangle edge sits at 25 % of the
 * texture from each side, the blur fades to nothing at the border (white on transparent).
 */
export function aoRectTexture() {
  const size = 128;
  const c = canvas(size);
  const g = c.getContext('2d');
  g.shadowColor = 'rgba(255,255,255,1)';
  g.shadowBlur = 22;
  g.shadowOffsetX = size * 4;
  g.fillStyle = '#fff';
  roundRect(g, size * 0.25 - size * 4, size * 0.25, size * 0.5, size * 0.5, 10);
  g.fill();
  return tex(c);
}

/** Radial glow for additive halos (lamps, screens, pulses). */
export function haloTexture() {
  const c = canvas(128);
  const g = c.getContext('2d');
  const grd = g.createRadialGradient(64, 64, 0, 64, 64, 64);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.18, 'rgba(255,255,255,0.55)');
  grd.addColorStop(0.5, 'rgba(255,255,255,0.14)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, 128, 128);
  return tex(c);
}

/** Faint square tiles, repeating (pod floors). White lines on transparent; tinted by the material. */
export function tileTexture() {
  const c = canvas(128);
  const g = c.getContext('2d');
  g.clearRect(0, 0, 128, 128);
  g.strokeStyle = 'rgba(255,255,255,1)';
  g.lineWidth = 2;
  g.strokeRect(1, 1, 126, 126);
  return tex(c, { repeat: true });
}

/** Wooden planks (walkways): grayscale, repeating along U. */
export function plankTexture() {
  const c = canvas(256, 64);
  const g = c.getContext('2d');
  g.fillStyle = '#ffffff';
  g.fillRect(0, 0, 256, 64);
  g.fillStyle = 'rgba(0,0,0,0.07)';
  for (let x = 0; x < 256; x += 32) g.fillRect(x, 0, 2, 64);
  g.fillStyle = 'rgba(0,0,0,0.035)';
  g.fillRect(0, 30, 256, 2);
  return tex(c, { repeat: true, srgb: true });
}

/** A rug: rounded, with a woven inner border. Grayscale, tinted by the material colour. */
export function rugTexture() {
  const s = 256;
  const c = canvas(s);
  const g = c.getContext('2d');
  g.clearRect(0, 0, s, s);
  g.fillStyle = '#ffffff';
  roundRect(g, 4, 4, s - 8, s - 8, 26);
  g.fill();
  g.strokeStyle = 'rgba(0,0,0,0.13)';
  g.lineWidth = 5;
  roundRect(g, 20, 20, s - 40, s - 40, 16);
  g.stroke();
  g.strokeStyle = 'rgba(0,0,0,0.07)';
  g.lineWidth = 2;
  roundRect(g, 30, 30, s - 60, s - 60, 12);
  g.stroke();
  return tex(c, { srgb: true });
}

/** Vertical alpha gradient: opaque at v = 0 (bottom), transparent at v = 1 (top). */
export function fadeTexture() {
  const c = canvas(4, 256);
  const g = c.getContext('2d');
  const grd = g.createLinearGradient(0, 256, 0, 0);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.55, 'rgba(255,255,255,0.55)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd;
  g.fillRect(0, 0, 4, 256);
  const t = tex(c);
  return t;
}

/**
 * Grey-level fade for `alphaMap` (three.js reads an alpha map's green channel, not its alpha):
 * white (opaque) at v = 0, black (transparent) at v = 1. `both`: transparent at both ends, opaque
 * in the middle (the glass shaft around the riding cabin).
 * @param {{both?: boolean}} [opts]
 */
export function alphaFadeTexture({ both = false } = {}) {
  const c = canvas(4, 256);
  const g = c.getContext('2d');
  const grd = g.createLinearGradient(0, 256, 0, 0);
  const stops = both
    ? [[0, 0], [0.3, 1], [0.7, 1], [1, 0]]
    : [[0, 1], [0.4, 0.85], [0.75, 0.3], [1, 0]];
  for (const [at, k] of stops) {
    const v = Math.round(k * 255);
    grd.addColorStop(at, `rgb(${v},${v},${v})`);
  }
  g.fillStyle = grd;
  g.fillRect(0, 0, 4, 256);
  return tex(c);
}

/**
 * Text painted around the hall: the floor name on the far arc, "ÉTAGE n" on the near arc.
 * The canvas is mapped on a flat ring; canvas "up" is the far side of the screen.
 * @param {HTMLCanvasElement|null} reuse  canvas to redraw into
 */
export function drawRingText(reuse, { name, label, color, line }) {
  const s = 1024;
  const c = reuse || canvas(s);
  const g = c.getContext('2d');
  g.clearRect(0, 0, s, s);
  const cx = s / 2;
  const cy = s / 2;
  const rText = s * 0.405;
  g.strokeStyle = line;
  g.lineWidth = 3;
  g.beginPath();
  g.arc(cx, cy, s * 0.475, 0, Math.PI * 2);
  g.stroke();
  g.lineWidth = 2;
  g.beginPath();
  g.arc(cx, cy, s * 0.335, 0, Math.PI * 2);
  g.stroke();
  g.fillStyle = color;
  g.font = `600 44px ${FONT_MONO}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  arcText(g, cx, cy, rText, String(name || '').toUpperCase(), -Math.PI / 2, true, 0.12);
  g.font = `500 34px ${FONT_MONO}`;
  arcText(g, cx, cy, rText, String(label || '').toUpperCase(), Math.PI / 2, false, 0.1);
  // Small ticks between both texts.
  for (const a of [0, Math.PI]) {
    g.beginPath();
    g.arc(cx + Math.cos(a) * rText, cy + Math.sin(a) * rText, 6, 0, Math.PI * 2);
    g.fill();
  }
  return c;
}

function arcText(g, cx, cy, r, text, centerAngle, outward, spacing) {
  const chars = [...text];
  const widths = chars.map((ch) => g.measureText(ch).width + spacing * 44);
  const total = widths.reduce((a, b) => a + b, 0);
  const maxArc = Math.PI * 0.86;
  let arc = total / r;
  const scale = arc > maxArc ? maxArc / arc : 1;
  arc = Math.min(arc, maxArc);
  // Outward (top arc) reads clockwise; inward (bottom arc) reads counter-clockwise.
  let a = outward ? centerAngle - arc / 2 : centerAngle + arc / 2;
  chars.forEach((ch, i) => {
    const w = (widths[i] / r) * scale;
    const mid = outward ? a + w / 2 : a - w / 2;
    g.save();
    g.translate(cx + Math.cos(mid) * r, cy + Math.sin(mid) * r);
    g.rotate(outward ? mid + Math.PI / 2 : mid - Math.PI / 2);
    if (scale < 1) g.scale(scale, 1);
    g.fillText(ch, 0, 0);
    g.restore();
    a = outward ? a + w : a - w;
  });
}

/** Wraps a canvas as an sRGB texture. */
export function canvasTexture(c) {
  return tex(c, { srgb: true });
}

/**
 * Status bubbles: 'waiting' (amber "!"), 'error' (red "!"), 'done' (green check).
 * @param {'waiting'|'error'|'done'} kind
 */
export function bubbleTexture(kind) {
  const s = 128;
  const c = canvas(s);
  const g = c.getContext('2d');
  const colors = { waiting: '#E9A23B', error: '#D9534F', done: '#3FA372' };
  const fill = colors[kind] || colors.waiting;
  // Soft drop shadow.
  g.fillStyle = 'rgba(40,30,20,0.18)';
  g.beginPath();
  g.arc(64, 70, 44, 0, Math.PI * 2);
  g.fill();
  // Bubble with a little tail.
  g.fillStyle = '#FFFDF8';
  g.beginPath();
  g.arc(64, 60, 46, 0, Math.PI * 2);
  g.fill();
  g.beginPath();
  g.moveTo(48, 98);
  g.lineTo(40, 122);
  g.lineTo(70, 102);
  g.closePath();
  g.fill();
  g.fillStyle = fill;
  g.beginPath();
  g.arc(64, 60, 38, 0, Math.PI * 2);
  g.fill();
  g.strokeStyle = '#FFFDF8';
  g.fillStyle = '#FFFDF8';
  g.lineCap = 'round';
  g.lineJoin = 'round';
  if (kind === 'done') {
    g.lineWidth = 11;
    g.beginPath();
    g.moveTo(45, 61);
    g.lineTo(58, 74);
    g.lineTo(83, 46);
    g.stroke();
  } else {
    g.lineWidth = 11;
    g.beginPath();
    g.moveTo(64, 38);
    g.lineTo(64, 64);
    g.stroke();
    g.beginPath();
    g.arc(64, 81, 6.5, 0, Math.PI * 2);
    g.fill();
  }
  return tex(c, { srgb: true });
}

export const GLYPHS = Object.freeze(['{ }', '</>', '#', '✦', '=', '01', '→', '✉', '%', '@', '∑', '*', '✓', '+', '::', '•']);

/** 4×4 atlas of small code-ish glyphs (white on transparent) for the working particles. */
export function glyphAtlas() {
  const s = 512;
  const cell = s / 4;
  const c = canvas(s);
  const g = c.getContext('2d');
  g.fillStyle = '#ffffff';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  GLYPHS.forEach((glyph, i) => {
    const x = (i % 4) * cell + cell / 2;
    const y = Math.floor(i / 4) * cell + cell / 2;
    const size = glyph.length > 2 ? 44 : glyph.length > 1 ? 54 : 70;
    g.font = `600 ${size}px ${FONT_MONO}`;
    g.fillText(glyph, x, y + 3);
  });
  const t = tex(c);
  t.flipY = false;
  return t;
}

/** Small elevator display ("▲ 2") drawn in a warm LED style. */
export function drawCabinDisplay(reuse, text) {
  const c = reuse || canvas(128, 64);
  const g = c.getContext('2d');
  g.fillStyle = '#1B1D22';
  g.fillRect(0, 0, 128, 64);
  g.fillStyle = '#FFB866';
  g.font = `600 38px ${FONT_MONO}`;
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.fillText(String(text), 64, 35);
  return c;
}
